import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { TagHttpService } from '@services/datasources/tag-http.service';
import { OrganizationAction } from '@state/organization.state';
import { AdvancedMeasureAction } from '@state/advanced-measure.state';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { TagEntitiesDto } from 'app/@types/dto/tag-entities-dto.model';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';

export interface EntityDropdownEntry {
    entity: OrganizationEntityDto;
    isDisabled: boolean;
}

export interface TagTreeNodeData {
    id: string;
    name: string;
    description?: string;
    parentId?: string;
    entities: OrganizationEntityDto[];
}

export interface RowNode {
    node: TreeNode<TagTreeNodeData>;
}

type TagSubType = 'P' | 'C';

type TagType = {
    name: string;
    key: TagSubType;
};

type RemovableItem = TagEntitiesDto | EntityDropdownEntry | OrganizationEntityDto;
type RemovableControl = 'childsTag' | 'linkedEntities' | 'linkedParentEntities';

interface TagFormControls {
    name: FormControl<string | null>;
    description: FormControl<string | null>;
    subTag: FormControl<TagSubType | null>;
    childsTag: FormControl<TagEntitiesDto[] | null>;
    linkedEntities: FormControl<EntityDropdownEntry[] | null>;
    parentTag: FormControl<TagEntitiesDto | null>;
    linkedParentEntities: FormControl<OrganizationEntityDto[] | null>;
}
@Component({
    selector: 'app-edit-tag',
    templateUrl: './edit-tag.component.html',
    styleUrls: ['./edit-tag.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class EditTagComponent implements OnInit {
    @Input() entities!: OrganizationEntityDto[];
    @Input() possibleParentTags!: TagEntitiesDto[];
    @Input() possibleChildTags!: TagEntitiesDto[];
    @Input() selectedRowNode?: RowNode | undefined;
    @Input() organizationId!: string;
    @Output() cancelled = new EventEmitter<void>();
    @Output() save = new EventEmitter<{ translateKey: string; status: boolean } | undefined>();

    tagForm: FormGroup<TagFormControls>;
    childsTagOptions?: { label: string; value: string }[];
    linkedEntitiesOptions?: { label: string; value: string }[];
    parentTagOptions?: { label: string; value: string }[];
    linkedParentEntitiesOptions?: { label: string; value: string }[];

    tagsType: TagType[] = [
        { name: this.translateService.instant('components.settingsForm.tagSection.form.typeTag1'), key: 'P' },
        { name: this.translateService.instant('components.settingsForm.tagSection.form.typeTag2'), key: 'C' },
    ];

    public possibleEntities?: OrganizationEntityDto[];
    public mandatoryEntities?: OrganizationEntityDto[];
    public entitiesDropdownEntries!: EntityDropdownEntry[];
    public selectedTag: TagEntitiesDto | undefined;
    public currentPossibleChildTags!: TagEntitiesDto[];

    constructor(
        private translateService: CustomTranslateService,
        private tagHttpService: TagHttpService,
        private store: Store,
        private confirmationService: ConfirmationService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.tagForm = new FormGroup<TagFormControls>({
            name: new FormControl<string | null>('', [Validators.required]),
            description: new FormControl<string | null>(''),
            subTag: new FormControl<TagSubType | null>('P', [Validators.required]),
            childsTag: new FormControl<TagEntitiesDto[] | null>([]),
            linkedEntities: new FormControl<EntityDropdownEntry[] | null>([]),
            parentTag: new FormControl<TagEntitiesDto | null>(null),
            linkedParentEntities: new FormControl<OrganizationEntityDto[] | null>([]),
        });
    }

    ngOnInit(): void {
        this.possibleEntities = this.entities;
        this.currentPossibleChildTags = [...this.possibleChildTags];
        if (this.selectedRowNode) {
            const selectedTag = this.transformTreeNodeToTagEntities(this.selectedRowNode.node);
            this.selectedTag = selectedTag;
            this.currentPossibleChildTags = this.currentPossibleChildTags.filter(
                (tag) => tag.tag.id !== selectedTag.tag.id,
            );
            this.possibleParentTags = this.possibleParentTags.filter((tag) => tag.tag.id !== selectedTag.tag.id);
        }
        this.entitiesDropdownEntries = this.entities.map((entity) => ({
            entity: entity,
            isDisabled: false,
        }));
        this.initFormValues();
    }

    transformTreeNodeToTagEntities(rowNode: TreeNode<TagTreeNodeData>): TagEntitiesDto {
        const data = rowNode.data as TagTreeNodeData;
        return {
            tag: {
                id: data.id,
                name: data.name,
                description: data.description,
                parentId: data.parentId,
            },
            entityIds: data.entities.map((entity) => entity.id ?? ''),
            childTags: rowNode.children
                ? rowNode.children.map((x) => this.transformTreeNodeToTagEntities(x))
                : undefined,
        };
    }

    initFormValues() {
        const selectedTag = this.selectedTag;
        if (!selectedTag) {
            return;
        }

        const linkedEntities = this.entitiesDropdownEntries.filter((entry) =>
            selectedTag.entityIds.includes(entry.entity.id ?? ''),
        );

        const controls = this.tagForm.controls;
        controls.name.setValue(selectedTag.tag.name);
        controls.description.setValue(selectedTag.tag.description ?? null);
        controls.subTag.setValue(selectedTag.tag.parentId ? 'C' : 'P');
        controls.childsTag.setValue(selectedTag.childTags ?? []);
        const parentTag: TagEntitiesDto | undefined = this.possibleParentTags.find(
            (tag) => tag.tag.id === selectedTag.tag.parentId,
        );
        if (parentTag) {
            controls.parentTag.setValue(parentTag);
            this.onParentTagChange(parentTag);
            controls.linkedParentEntities.setValue(linkedEntities.map((entry) => entry.entity));
        } else {
            if (selectedTag.childTags && selectedTag.childTags.length > 0) {
                this.currentPossibleChildTags.push(...selectedTag.childTags);
                controls.subTag.disable();
            }
            controls.linkedEntities.setValue(linkedEntities);
            this.onChildTagChange(controls.childsTag.value ?? []);
        }
    }

    get subTag(): TagSubType | null {
        return this.tagForm.controls.subTag.value;
    }

    /*
    Methods to handle submition of the form
    */
    onCancel() {
        this.cancelled.emit();
    }

    onSubmit() {
        // A child tag also needs its parent, checked outside the validators: touching the fields
        // shows that error too.
        if (!revealFormErrors(this.tagForm, this.host.nativeElement) || !this.entitiesValidated()) {
            this.tagForm.markAllAsTouched();
            return;
        }

        const controls = this.tagForm.controls;
        const subTag = controls.subTag.value;

        const newTag: TagEntitiesDto = {
            tag: {
                id: this.selectedTag?.tag.id as string,
                name: controls.name.value ?? '',
                description: controls.description.value ?? undefined,
                parentId: subTag === 'C' ? controls.parentTag.value?.tag.id : undefined,
            },
            entityIds:
                subTag === 'C'
                    ? (controls.linkedParentEntities.value ?? []).length > 0
                        ? (controls.linkedParentEntities.value ?? []).map((entity) => entity.id ?? '')
                        : (this.possibleEntities ?? []).map((entity) => entity.id ?? '')
                    : (controls.linkedEntities.value ?? []).length > 0
                      ? (controls.linkedEntities.value ?? []).map((entry) => entry.entity.id ?? '')
                      : this.entities.map((entity) => entity.id ?? ''),
            childTags: subTag === 'P' ? (controls.childsTag.value ?? []) : [],
        };

        if (this.selectedTag) {
            this.editTag(newTag);
        } else {
            this.createTag(newTag);
        }
    }

    private createTag(newTag: TagEntitiesDto) {
        this.tagHttpService.create(newTag, this.organizationId).subscribe({
            next: (res: TagEntitiesDto[]) => {
                this.store.dispatch(new OrganizationAction.SetTags(res));
                this.save.emit({
                    translateKey: 'components.settingsForm.tagSection.toasts.creationSuccess',
                    status: true,
                });
            },
        });
    }

    private editTag(newTag: TagEntitiesDto) {
        this.tagHttpService.update(newTag, this.organizationId).subscribe({
            next: (res: TagEntitiesDto[]) => {
                this.store.dispatch(new OrganizationAction.SetTags(res));
                this.save.emit({
                    translateKey: 'components.settingsForm.tagSection.toasts.updateSuccess',
                    status: true,
                });
            },
        });
    }
    public onDelete() {
        const selectedTag = this.selectedTag;
        if (!selectedTag) {
            return;
        }

        const deleteMessage =
            selectedTag.childTags && selectedTag.childTags.length > 0
                ? this.translateService.instant('components.settingsForm.tagSection.toasts.confirm_messageWithChildren')
                : this.translateService.instant('components.settingsForm.tagSection.toasts.confirm_message');

        this.confirmationService.confirm(
            destructiveConfirmation({
                message: deleteMessage,
                header: this.translateService.instant('common.confirm_title'),
                acceptLabel: this.translateService.instant('common.confirm_button'),
                rejectLabel: this.translateService.instant('common.cancel_button'),
                icon: 'ms ms-warning',
                accept: () => {
                    if (selectedTag.tag.id) {
                        this.tagHttpService.delete(selectedTag.tag.id, this.organizationId).subscribe({
                            next: (res: TagEntitiesDto[]) => {
                                this.store.dispatch(new OrganizationAction.SetTags(res));
                                this.store.dispatch(new AdvancedMeasureAction.ClearState());
                                this.save.emit({
                                    translateKey: 'components.settingsForm.tagSection.toasts.deleteSuccess',
                                    status: true,
                                });
                            },
                            error: () => {
                                this.save.emit({
                                    translateKey: 'components.settingsForm.tagSection.toasts.deleteFail',
                                    status: false,
                                });
                            },
                        });
                    }
                },
            }),
        );
    }

    /*
     *Methods to handle the dynamicallity of the entities in the form
     */
    public onParentTagChange(event: TagEntitiesDto) {
        const parentEntityIds = event.entityIds;
        this.possibleEntities = this.entities.filter((entity) => parentEntityIds.includes(entity.id ?? ''));

        const linkedParentEntitiesControl = this.tagForm.controls.linkedParentEntities;
        const currentValue = linkedParentEntitiesControl.value ?? [];
        const filteredEntities: OrganizationEntityDto[] = currentValue.filter((entity) =>
            parentEntityIds.includes(entity.id ?? ''),
        );

        linkedParentEntitiesControl.setValue(filteredEntities);
    }

    public onChildTagChange(selectedTags: TagEntitiesDto[]) {
        const mandatoryEntities = this.entities.filter((entity) =>
            selectedTags.some((tag) => tag.entityIds.includes(entity.id ?? '')),
        );

        const currentLinkedEntities = this.tagForm.controls.linkedEntities.value ?? [];

        const existingNonMandatory = currentLinkedEntities.filter(
            (entry) => !mandatoryEntities.some((mandEntity) => mandEntity.id === entry.entity.id),
        );

        if (mandatoryEntities.length === 0) {
            this.tagForm.controls.linkedEntities.setValue([
                ...currentLinkedEntities.map((entry) => ({
                    entity: entry.entity,
                    isDisabled: false,
                })),
            ]);
            this.entitiesDropdownEntries = this.entities.map((entity) => ({
                entity: entity,
                isDisabled: false,
            }));
            return;
        }

        this.tagForm.controls.linkedEntities.setValue([
            ...existingNonMandatory.map((entry) => ({
                entity: entry.entity,
                isDisabled: false,
            })),
            ...mandatoryEntities.map((entity) => ({
                entity: entity,
                isDisabled: true,
            })),
        ]);

        this.entitiesDropdownEntries = this.entities.map((entity) => ({
            entity: entity,
            isDisabled: mandatoryEntities.some((mandEntity) => mandEntity.id === entity.id),
        }));
    }

    public removeSelectedItem(item: RemovableItem, control: RemovableControl): void {
        if (control === 'childsTag') {
            const current = this.tagForm.controls.childsTag.value ?? [];
            const filtered = current.filter((i) => i !== item);
            this.tagForm.controls.childsTag.setValue(filtered);
            this.onChildTagChange(filtered);
        } else if (control === 'linkedEntities') {
            const current = this.tagForm.controls.linkedEntities.value ?? [];
            this.tagForm.controls.linkedEntities.setValue(current.filter((i) => i !== item));
        } else {
            const current = this.tagForm.controls.linkedParentEntities.value ?? [];
            this.tagForm.controls.linkedParentEntities.setValue(current.filter((i) => i !== item));
        }
    }

    public entitiesValidated(): boolean {
        if (this.subTag === 'C') {
            return !!this.tagForm.controls.parentTag.value;
        }
        return true;
    }
}
